import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { StaticPathUtil } from '@app/core/util/static-path.util';
import { StringUtil } from '../util/string.util';

@Injectable()
export class RouterService {
  constructor(private router: Router, private staticPath: StaticPathUtil) {}

  public navigate(command: any, extras?: NavigationExtras): void {
    this.router.navigate(command, extras);
  }

  public navigateURL(url: string): void {
    if (url) {
      this.router.navigate([url]);
    }
  }

  public navigateFullUrlNewTab(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  public navigateExtern(url: string): void {
    if (url) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
      } else {
        window.open('https://' + url, '_blank');
      }
    }
  }

  public openMail(mail: string): void {
    if (mail) {
      location.href = 'mailto:' + mail;
    }
  }

  public navigateTodoOverview(mode: 'Meine' | 'Arge' | 'Teilnehmer'): void {
    this.router.navigate(['/todo-overview'], {queryParams: {mode: mode}});
  }

  public navigateMaklerTodoOverview(): void {
    this.router.navigate(['/todo-overview/makler']);
  }

  public navigateHome(): void {
    this.router.navigate(['/home']);
  }

  public navigateHomeNewTab(): void {
    window.open('/', '_blank');
  }

  public navigateAdrChange(perS: string, personName?: string): void {
    this.router.navigate(['/person/services/change-adresse/' + perS], {queryParams: {name: personName}});
  }

  public navigateAdrChangeMakler(perS: string, personName?: string): void {
    this.router.navigate(['/maklerperson/services/change-adresse/' + perS], {queryParams: {name: personName}});
  }

  public navigateAddAdresse(perS: string): void {
    this.router.navigate(['/person/services/add-adresse/' + perS]);
  }

  public navigateMaklerAddAdresse(perS: string): void {
    this.router.navigate(['/maklerperson/services/add-adresse/' + perS]);
  }

  public navigateAdrChangeForSingleAdresse(perS: string, adrId: number): void {
    this.router.navigate(['/person/services/change-adresse/' + perS + '/adr/' + adrId]);
  }

  public navigateMaklerAdrChangeForSingleAdresse(perS: string, adrId: number): void {
    this.router.navigate(['/maklerperson/services/change-adresse/' + perS + '/adr/' + adrId]);
  }

  public navigateChangeZahlweisePers(perS: string): void {
    this.router.navigate(['/person/services/change-zahlweise/' + perS]);
  }

  public navigateChangeZahlweise(perS: string, beitragszahlerId: string): void {
    this.router.navigate(['/person/services/change-zahlweise/' + perS + '/btr/' + beitragszahlerId]);
  }

  public navigateChangeHauptfaelligkeit(perS: string, beitragszahlerId: string): void {
    this.router.navigate(['/person/services/change-hauptfaelligkeit/' + perS]);
  }

  public navigateAddSpartenentfall(perS: string, polId: number): void {
    this.router.navigate(['/police/services/add-spartenentfall/' + perS + '/polizze/' + polId]);
  }

  public navigateAddStorno(perS: string, polId: number): void {
    this.router.navigate(['/police/services/add-storno/' + perS + '/polizze/' + polId]);
  }

  public navigateMaklerAddStorno(perS: string, polId: number): void {
    this.router.navigate(['/maklerpolizze/services/add-storno/' + perS + '/polizze/' + polId]);
  }

  public navigateKaskokumstellung(perS: string, polId: number): void {
    this.router.navigate(['/police/services/kaskoumstellung/' + perS + '/polizze/' + polId]);
  }

  public navigateMaklerKaskokumstellung(perS: string, polId: number): void {
    this.router.navigate(['/maklerpolizze/services/kaskoumstellung/' + perS + '/polizze/' + polId]);
  }

  public navigateWkzAbmldung(perS: string, polId: number): void {
    this.router.navigate(['/person/services/wkz-abmeldung/' + perS + '/polizze/' + polId]);
  }

  public navigateAdrKorrForOverview(perS: string): void {
    this.router.navigate(['/person/services/korr-adresse/' + perS]);
  }

  public navigateAdrKorrForSingleAdresse(perS: string, adrId: number): void {
    this.router.navigate(['/person/services/korr-adresse/' + perS + '/adr/' + adrId]);
  }

  public navigateMaklerAdrKorrForOverview(perS: string): void {
    this.router.navigate(['/maklerperson/services/korr-adresse/' + perS]);
  }

  public navigateMaklerAdrKorrForSingleAdresse(perS: string, adrId: number): void {
    this.router.navigate(['/maklerperson/services/korr-adresse/' + perS + '/adr/' + adrId]);
  }

  public navigateVertreterDetail(vertreterId: string): void {
    this.router.navigate(['/vertreter/' + vertreterId]);
  }

  public navigateVertreterSearch(): void {
    this.router.navigate(['/vertreter']);
  }

  public navigateTextDetail(txtId: string, art: string, sprache: string): void {
    this.router.navigate(['/texte/' + txtId + '/art/' + art + '/sprache/' + sprache]);
  }

  public navigateFahrzeugDetail(fahrzeugId: string): void {
    this.router.navigate(['/fahrzeug/' + fahrzeugId]);
  }

  public navigateFahrzeugDetailVersion(fzID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'fahrzeug',
      fzID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateHaushaltDetail(adresseId: string): void {
    this.router.navigate(['/haushalt/' + adresseId]);
  }

  public navigateGebaeudeDetail(adresseId: string): void {
    this.router.navigate(['/gebaeude/' + adresseId]);
  }

  public navigateTodoDetail(todoId: string | number): void {
    this.router.navigate(['/todo-detail/nr/' + todoId]);
  }

  public navigateMaklerTodoDetail(todoId: string | number): void {
    this.router.navigate(['/todo-detail/makler/nr/' + todoId]);
  }

  public navigateArticleOverview(): void {
    this.router.navigate(['/article-overview']);
  }

  public navigateSettings(): void {
    this.router.navigate(['/settings']);
  }

  public navigateSettingsBetreuerChange(): void {
    this.router.navigate(['/settings'], {queryParams: {section: 'betreuer'}});
  }

  public navigateContactOverview(): void {
    this.router.navigate(['/contact-overview']);
  }

  public navigateContactOverviewWithSearch(query: string): void {
    this.router.navigate(['/contact-overview'], {queryParams: {search: query}});
  }

  public navigateFormDemo(): void {
    this.router.navigate(['/form-demo']);
  }

  public navigatePersonDetail(persID: number): void {
    this.router.navigate(['/person/' + persID]);
  }

  public navigateBeitragszahlerDetail(persID: number): void {
    this.router.navigate(['/beitragszahler/' + persID]);
  }

  public navigateVersichertepersonDetail(persID: number): void {
    this.router.navigate(['/versicherteperson/' + persID]);
  }

  public navigateMaklerPersonDetail(persID: number): void {
    this.router.navigate(['/maklerperson/' + persID]);
  }

  public navigateNews(): void {
    this.router.navigate(['/news']);
  }

  public navigateNewsDetail(newsID: number): void {
    this.router.navigate(['/news/detail/' + newsID]);
  }

  public navigateMaklerPolizzeDetail(polint: number, additionalRoute?: string): void {
    const route = [
      'maklerpolizze',
      polint
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateMaklerSchadenDetail(schadenId: number): void {
    this.router.navigate(['/maklerschaden/' + schadenId]);
  }

  public navigateMaklerKontakte(): void {
    this.router.navigate(['/maklerkontakte']);
  }

  public navigateHotlineDetail(persID: number): void {
    this.router.navigate(['/hotline/' + persID]);
  }

  public navigateHotlineDetailWithQueryParams(persID: number, qParams?: any): void {
    this.router.navigate(['/hotline/' + persID], {queryParams: qParams});
  }

  public navigateAssistanceDetail(persID: number): void {
    this.router.navigate(['/assistance/' + persID]);
  }

  public navigateAssistanceDetailWithQueryParams(persID: number, qParams?: any): void {
    this.router.navigate(['/assistance/' + persID], {queryParams: qParams});
  }

  public navigateAssistanceDetailPolizze(persId: number, polizzeID: number): void {
    this.router.navigate(['/assistance/' + persId + '/polizze/' + polizzeID]);
  }

  public navigatePersonDetailDokumente(persID: number): void {
    this.router.navigate(['/person/' + persID + '/documents']);
  }

  public navigatePersonDetailDocumentCreate(persID: number): void {
    this.router.navigate(['/person/' + persID + '/documents/create']);
  }

  public navigatePersonDetailVollmacht(persID: number): void {
    this.router.navigate(['/person/' + persID + '/vollmacht']);
  }

  public navigatePersonDetailVollmachtCreate(persID: number): void {
    this.router.navigate(['/person/' + persID + '/vollmacht/create']);
  }

  public navigatePersonSearch(): void {
    this.router.navigate(['/person']);
  }

  public navigateSchadenDetail(schadenId: number, additionalRoute?: string): void {
    const route = [
      'schaden',
      schadenId
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigatePolizzeSearch(): void {
    this.router.navigate(['/polizze']);
  }

  public navigateSearchQueryParams(route: string, qParams?: any): void {
    this.router.navigate(['/' + route], {
      replaceUrl:  true,
      queryParams: qParams
    });
  }

  public navigatePersonDetailVersion(persID: number, versID: number, additionalRoute?: string): void {
    if (!versID) {
      this.router.navigate(['/person/' + persID]);
    } else {
      const route = [
        'person',
        persID,
        'version',
        versID
      ];
      if (additionalRoute) {
        route.push(additionalRoute);
      }
      this.router.navigate(route);
    }
  }

  public navigateBeitragszahlerDetailVersion(persID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'beitragszahler',
      persID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateVersichertepersonDetailVersion(persID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'versicherteperson',
      persID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateBetreuerDetailVersion(persID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'betreuer',
      persID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateVertreterDetailVersion(persID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'vertreter',
      persID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateGebaeudeDetailVersion(gebID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'gebaeude',
      gebID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public openInfoClientURLPolizze(polint: number): void {
    window.location.href = this.staticPath.getInfoClientURLPol() + polint;
  }

  public openArchivClientDoc(docId: string): void {
    window.location.href = this.staticPath.getArchivClientUrl() + docId;
  }

  public openInfoClientURLSchaden(schints: number): void {
    window.location.href = this.staticPath.getInfoClientURLSchaden() + schints;
  }

  public navigatePoliceDetail(polint: number, additionalRoute?: string): void {
    const route = [
      'polizze',
      polint
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigatePoliceDetailBuchungenTermine(polint: number): void {
    this.router.navigate(['/polizze/' + polint + '/buchungen'], {queryParams: {section: 'termine'}});
  }

  public navigatePoliceBuchungDetail(polint: number, buchungId: number): void {
    this.router.navigate(['/polizze/' + polint + '/buchung/' + buchungId]);
  }

  public navigatePoliceBriefeWithDoctyp(polint: number, doctypfilter: string): void {
    this.router.navigate(['/polizze/' + polint + '/briefe'], {queryParams: {doctyp: doctypfilter}});
  }

  public navigatePoliceVertrag(polint: number, vernr: number): void {
    this.router.navigate(['/polizze/' + polint + '/vertrag/' + vernr]);
  }

  public navigateVertragPosition(polint: number, vernr: number, posnr: number): void {
    this.router.navigate(['/polizze/' + polint + '/vertrag/' + vernr + '/position/' + posnr]);
  }

  public navigateVsbDetail(vsbId: string): void {
    this.router.navigate(['/vsb/' + vsbId]);
  }

  public navigateBetreuerDetail(personId: string): void {
    this.router.navigate(['/betreuer/' + personId]);
  }

  public navigateMaklerbetreuerDetail(personId: string): void {
    this.router.navigate(['/maklerbetreuer/' + personId]);
  }

  public navigateMaklervertreterDetail(personId: string): void {
    this.router.navigate(['/maklervertreter/' + personId]);
  }

  public navigateMaklerVBDruck(personId: string): void {
    this.router.navigate(['/maklervsb/' + personId + '/druck']);
  }

  public navigateBetreuerknotenDetail(personId: string): void {
    this.router.navigate(['/maklerknoten/' + personId]);
  }

  public navigateFamilienpassDetail(personId: string): void {
    this.router.navigate(['/familienpass/' + personId]);
  }

  public navigateMaklerFamilienpassDetail(personId: string): void {
    this.router.navigate(['/maklerfamilienpass/' + personId]);
  }

  public navigatePartnerDetail(personId: string): void {
    this.router.navigate(['/partner/' + personId]);
  }

  public navigatePartnerDetailVersion(id: number, versID: number, additionalRoute?: string): void {
    const route = [
      'partner',
      id,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateVerbundDetail(personId: string): void {
    this.router.navigate(['/verbund/' + personId]);
  }

  public navigateAssistancePolice(personId: number, polizzeId: number): void {
    this.router.navigate(['/assistance/' + personId + '/polizze/' + polizzeId]);
  }

  public navigateZahlungDetail(polizzeId: number, zahlungId: number): void {
    this.router.navigate(['/polizze/' + polizzeId + '/zahlung/' + zahlungId]);
  }

  public navigatePolizzebuchungDetail(polizzeId: number, buchungId: number): void {
    this.router.navigate(['/polizze/' + polizzeId + '/buchung/' + buchungId]);
  }

  public navigateSchadenzahlungEmpfDetail(personId: string): void {
    this.router.navigate(['/schadenzahlungempf/' + personId]);
  }

  public navigateSchadenbeteiligterDetail(personId: number, kfzKz?: string, fremndAnstaltNr?: string, fremndAnstaltPolNr?: string): void {
    this.router.navigate([StringUtil.getSchadenbeteiligterNavigationStr(personId, kfzKz, fremndAnstaltNr, fremndAnstaltPolNr)]);
  }

  public navigateSchadenFeedbackDetail(schadenId: string, feedbackId: string): void {
    this.router.navigate([`/schaden/${schadenId}/feedback/`], {queryParams: {feedbackId}});
  }

  public navigateZulassungDetail(id: string, index: number): void {
    this.router.navigate(['/zulassung/' + id + '/index/' + index]);
  }

  public navigateZulassungArchivKennzeichenOnly(kennzeichen: string): void {
    this.router.navigate(['/zulassung/kennzeichen/' + kennzeichen]);
  }

  public navigateBezugsberechtigterDetail(personId: string): void {
    this.router.navigate(['/bezugsberechtigter/' + personId]);
  }

  public navigateBezugsberechtigterDetailVersion(persID: number, versID: number, additionalRoute?: string): void {
    const route = [
      'bezugsberechtigter',
      persID,
      'version',
      versID
    ];
    if (additionalRoute) {
      route.push(additionalRoute);
    }
    this.router.navigate(route);
  }

  public navigateUserAdminDetail(personId: string): void {
    this.router.navigate(['/benutzer/' + personId]);
  }

  public navigateGenehmigungDetail(typ: string, genehmigungId: string): void {
    this.router.navigate([`/genehmigung/${typ}/${genehmigungId}`]);
  }

  public navigateKundenGeburtstag(): void {
    this.router.navigate([`/kundengeburtstag`]);
  }

  public navigateMaklerBedingungen(): void {
    this.router.navigate([`/maklerdocuments/list/bedingungen`]);
  }

  public navigateMaklerProduktdatenblatt(): void {
    this.router.navigate([`/maklerdocuments/list/produktdatenblatt`]);
  }

  public navigateMaklerExcelrechner(): void {
    this.router.navigate([`/maklerexcel`]);
  }

  public navigateDatenschutz(): void {
    this.router.navigate([`/datenschutz`]);
  }

  public navigateDatenschutzAkt(dsAktId?: string): void {
    dsAktId ? this.router.navigate([`/datenschutz/akt/${dsAktId}`]) : this.router.navigate([`/datenschutz/akt`]);
  }

  public navigateDatenschutzReport(dsAktId: string): void {
    this.router.navigate([`/datenschutz/report/${dsAktId}`]);
  }

  public navigateKundendatenadminDetail(personId: string): void {
    this.router.navigate(['/kundendatenadmin/ersetzen/' + personId]);
  }

  public navigateKundendatenadminVerbundDetail(personId: string): void {
    this.router.navigate(['/kundendatenadmin/verbund/' + personId]);
  }

  public navigateKundendatenadminVerbund(): void {
    this.router.navigate(['/kundendatenadmin/verbund']);
  }

  public navigateKundendatenadminErsetzen(): void {
    this.router.navigate(['/kundendatenadmin/ersetzen']);
  }

  public navigateUngeklaerteZahlungArchiv(opostenId: string): void {
    this.router.navigate(['/ungeklaertezahlungen/archiv/' + opostenId]);
  }

  public navigateMaklerDocumentsWithSearchNewTab(search: string): void {
    this.navigateFullUrlNewTab('/maklerdocuments/list/dokumente?search=' + encodeURI(search));
  }
}
